import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, AlertTriangle } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { IconField, FormAlert } from "@/components/auth/IconField";

const InvalidLinkView = () => {
  const { t } = useTranslation();
  return (
    <AuthLayout
      icon={AlertTriangle}
      title={t("resetPassword.invalidLinkTitle")}
      subtitle={t("resetPassword.invalidLinkSubtitle")}
      footer={
        <Link to="/forgot-password" data-testid="reset-request-new" className="text-primary font-medium hover:underline">
          {t("resetPassword.requestNew")}
        </Link>
      }
    >
      <p data-testid="reset-invalid-link" className="text-sm text-foreground text-center">{t("resetPassword.invalidLinkMessage")}</p>
    </AuthLayout>
  );
};

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) return setError(t("resetPassword.passwordsDontMatch"));
    setLoading(true);
    try {
      await base44.auth.resetPassword({ resetToken, newPassword });
      window.location.href = "/login";
    } catch (err) {
      setError(err.message || t("resetPassword.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  if (!resetToken) return <InvalidLinkView />;

  return (
    <AuthLayout icon={Lock} title={t("resetPassword.title")} subtitle={t("resetPassword.subtitle")}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormAlert message={error} testId="reset-error" />
        <IconField id="password" icon={Lock} label={t("resetPassword.newPassword")} data-testid="reset-password-input" type="password"
          autoComplete="new-password" autoFocus placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
        <IconField id="confirm" icon={Lock} label={t("common.confirmPassword")} data-testid="reset-confirm-input" type="password"
          autoComplete="new-password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
        <Button type="submit" data-testid="reset-submit" className="w-full h-12 font-medium" disabled={loading}>
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("resetPassword.submitting")}</> : t("resetPassword.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
